// src/components/Features.js — Light Theme
import React from 'react';
import styled from 'styled-components';
import {
  FaChartLine,
  FaBrain,
  FaRegListAlt,
  FaShieldAlt,
  FaBell,
  FaMobileAlt,
} from 'react-icons/fa';
import { motion } from 'framer-motion';

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.12, duration: 0.55, ease: 'easeOut' },
  }),
};

const featuresData = [
  {
    icon: <FaBrain />,
    tag: 'AI Powered',
    title: 'AI Crypto Signals — Free & Accurate',
    description:
      'Trend Genie delivers free AI crypto signals powered by advanced machine learning. Get automated entry, stop-loss, and take-profit levels for every trade setup.',
    color: '#29ABE2',
    bg: '#e4f5fc',
  },
  {
    icon: <FaChartLine />,
    tag: 'Real-Time',
    title: 'Live Market Charting & Analysis',
    description:
      'Analyze live crypto markets with precision charting tools. Our AI scans price action in real time to surface the best setups for both beginners and expert traders.',
    color: '#0ea5e9',
    bg: '#e0f2fe',
  },
  {
    icon: <FaBell />,
    tag: 'Instant Alerts',
    title: 'Real-Time Trade Alerts on Android',
    description:
      'Never miss a signal. Trend Genie sends instant push notifications when our AI detects high-probability crypto trade setups on your Android device.',
    color: '#f59e0b',
    bg: '#fef3c7',
  },
  {
    icon: <FaShieldAlt />,
    tag: 'DeFi Safety',
    title: 'RugShield — DeFi Token Scanner',
    description:
      'Before you buy any token, scan it with RugShield. Detect honeypots, rug pulls, and smart contract risks instantly. The best crypto project safety tool.',
    color: '#10b981',
    bg: '#d1fae5',
  },
  {
    icon: <FaRegListAlt />,
    tag: 'Community',
    title: 'Web3 Social Platform',
    description:
      'Join Trend Genie Social — a crypto-focused community platform with gamified rewards, daily check-ins, and an airdrop program for active participants.',
    color: '#F7941D',
    bg: '#fff4e5',
  },
  {
    icon: <FaMobileAlt />,
    tag: 'Mobile First',
    title: 'Best Free Crypto App for Android',
    description:
      'Available free on Google Play, Trend Genie is designed mobile-first for crypto traders who want AI signals, charts, and DeFi tools in one clean interface.',
    color: '#ec4899',
    bg: '#fce7f3',
  },
];

const Features = () => {
  return (
    <FeaturesContainer id="features">
      <SectionHeader>
        <SectionBadge>Why Trend Genie</SectionBadge>
        <SectionTitle>
          Everything You Need to Trade{' '}
          <GradientText>Smarter with AI</GradientText>
        </SectionTitle>
        <SectionSubtitle>
          Trend Genie is the best free AI crypto signals app — combining real-time market
          analysis, automated trade setups, DeFi safety tools, and a Web3 community in one
          powerful platform.
        </SectionSubtitle>
      </SectionHeader>

      <FeaturesGrid>
        {featuresData.map((feature, index) => (
          <FeatureCard
            key={index}
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            custom={index}
          >
            <CardTop>
              <IconWrapper color={feature.color} bg={feature.bg}>
                {feature.icon}
              </IconWrapper>
              <CardTag color={feature.color} bg={feature.bg}>
                {feature.tag}
              </CardTag>
            </CardTop>
            <FeatureTitle>{feature.title}</FeatureTitle>
            <FeatureDescription>{feature.description}</FeatureDescription>
          </FeatureCard>
        ))}
      </FeaturesGrid>

      {/* Stats Strip */}
      <StatsStrip>
        {[
          { value: '10,000+', label: 'Active Traders' },
          { value: 'Free', label: 'Crypto Signals App' },
          { value: '5★', label: 'Avg. App Rating' },
          { value: 'Android', label: 'Available Now' },
        ].map((stat, i) => (
          <StatItem key={i}>
            <StatValue>{stat.value}</StatValue>
            <StatLabel>{stat.label}</StatLabel>
          </StatItem>
        ))}
      </StatsStrip>
    </FeaturesContainer>
  );
};

export default Features;

/* ---- STYLES ---- */

const FeaturesContainer = styled.section`
  padding: 6rem 10%;
  background: var(--bg-secondary);
  text-align: center;
  position: relative;
`;

const SectionHeader = styled.div`
  max-width: 680px;
  margin: 0 auto 4rem;
`;

const SectionBadge = styled.span`
  display: inline-block;
  background: var(--primary-light);
  color: var(--primary-color);
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  padding: 0.4rem 1rem;
  border-radius: 999px;
  margin-bottom: 1rem;
`;

const SectionTitle = styled.h2`
  font-size: 2.6rem;
  font-weight: 800;
  color: var(--text-color);
  line-height: 1.2;
  letter-spacing: -0.5px;
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const GradientText = styled.span`
  background: linear-gradient(135deg, #29ABE2, #F7941D);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const SectionSubtitle = styled.p`
  font-size: 1.05rem;
  color: var(--text-secondary-color);
  line-height: 1.75;
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
`;

const FeatureCard = styled(motion.div)`
  background: var(--bg-surface);
  border: 1px solid var(--border-color);
  padding: 2.2rem 1.8rem;
  border-radius: 18px;
  text-align: left;
  box-shadow: var(--shadow-sm);
  transition: all 0.35s ease;

  &:hover {
    transform: translateY(-6px);
    box-shadow: var(--shadow-lg);
    border-color: transparent;
  }
`;

const CardTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.2rem;
`;

const IconWrapper = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 14px;
  background: ${p => p.bg};
  color: ${p => p.color};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.4rem;
`;

const CardTag = styled.span`
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: ${p => p.color};
  background: ${p => p.bg};
  padding: 0.3rem 0.7rem;
  border-radius: 999px;
`;

const FeatureTitle = styled.h3`
  font-size: 1.15rem;
  font-weight: 700;
  color: var(--text-color);
  margin-bottom: 0.7rem;
  line-height: 1.3;
`;

const FeatureDescription = styled.p`
  color: var(--text-secondary-color);
  line-height: 1.7;
  font-size: 0.95rem;
`;

const StatsStrip = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.5rem;
  margin-top: 4rem;
  padding: 2.5rem 3rem;
  background: var(--bg-surface);
  border: 1px solid var(--border-color);
  border-radius: 20px;
  box-shadow: var(--shadow-md);

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    padding: 2rem 1.5rem;
  }
`;

const StatItem = styled.div`
  text-align: center;
`;

const StatValue = styled.div`
  font-size: 2rem;
  font-weight: 800;
  background: linear-gradient(135deg, #29ABE2, #F7941D);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 0.25rem;
`;

const StatLabel = styled.div`
  font-size: 0.88rem;
  color: var(--text-secondary-color);
  font-weight: 500;
`;
